import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  brokers,
  invitations,
  type GrantableRole,
  type Invitation,
  type InvitationIssued,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { UserFormModal } from './UserFormModal';

/**
 * Convites de acesso.
 *
 * Convidar existe porque criar a pessoa com uma senha escolhida por quem administra
 * e o caminho errado em produto multiempresa: a senha permanente passaria por quem
 * nao e o dono dela, e ficaria conhecida por duas pessoas. Aqui quem administra
 * decide quem entra e com qual perfil; a senha nasce com o convidado.
 *
 * Convidar e o mesmo formulario do Cadastrar Usuario (P-USU-1), com CPF, telefone e
 * endereco. A lista mostra os perfis, a corretora e as datas de aceite e de
 * revogacao de cada convite, que a API ja devolvia e a tela nao mostrava.
 *
 * O endereco de aceite aparece uma unica vez, no retorno de quem convidou. Ele nao
 * volta em nenhuma consulta: a lista de convites nao pode virar um caminho para
 * recuperar o acesso de outra pessoa.
 */
export interface InvitationsPanelProps {
  /**
   * Perfis oferecidos no convite. Para quem administra a corretora, sao so os dois
   * dela, e o convite nao escolhe corretora: o servidor prende o convite a corretora
   * da sessao.
   */
  papeis: GrantableRole[];
  /** A sessao e de quem administra a corretora. */
  daCorretora?: boolean;
}

export function InvitationsPanel({
  papeis,
  daCorretora = false,
}: InvitationsPanelProps) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeConvidar = can('usuarios.usuario.create');
  const podeRevogar = can('usuarios.usuario.create');

  const [convidando, setConvidando] = useState(false);
  const [emitido, setEmitido] = useState<InvitationIssued | null>(null);
  const [revogando, setRevogando] = useState<Invitation | null>(null);

  const consulta = useQuery({
    queryKey: ['invitations'],
    queryFn: ({ signal }) => invitations.list(signal),
  });
  const corretoras = useQuery({
    queryKey: ['brokers', 'options'],
    queryFn: ({ signal }) => brokers.list({ limit: 100, stage: 'ativo' }, signal),
    enabled: !daCorretora && can('corretores.corretor.view'),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    void queryClient.invalidateQueries({ queryKey: ['members'] });
  }

  const reenviar = useMutation({
    mutationFn: (invitationId: string) => invitations.resend(invitationId),
    onSuccess: (resultado) => {
      setEmitido(resultado);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const revogar = useMutation({
    mutationFn: (invitationId: string) => invitations.revoke(invitationId),
    onSuccess: () => {
      toast.success('Convite revogado. O endereco enviado deixa de valer.');
      setRevogando(null);
      invalidar();
    },
    onError: (erro: Error) => {
      toast.error(erro.message);
      setRevogando(null);
    },
  });

  const nomeDoPerfil = new Map(papeis.map((papel) => [papel.id, papel.name]));
  const nomeDaCorretora = new Map(
    (corretoras.data?.items ?? []).map((corretora) => [
      corretora.id,
      corretora.legal_name,
    ]),
  );

  const colunas: Column<Invitation>[] = [
    {
      key: 'pessoa',
      header: 'Pessoa',
      render: (convite) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{convite.name}</p>
          <p className="truncate text-2xs text-ink-500">{convite.email}</p>
        </div>
      ),
    },
    {
      key: 'perfis',
      header: 'Perfis',
      hideBelow: 'sm',
      render: (convite) => (
        <div className="flex flex-wrap gap-1">
          {convite.role_ids.map((roleId) => (
            <Badge key={roleId} tone="neutral">
              {nomeDoPerfil.get(roleId) ?? 'perfil da empresa'}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'corretora',
      header: 'Corretora',
      hideBelow: 'md',
      render: (convite) =>
        convite.broker_id ? (
          (nomeDaCorretora.get(convite.broker_id) ?? 'Corretora ligada')
        ) : (
          <span className="text-ink-400">sem corretora</span>
        ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (convite) => (
        <div className="flex flex-col items-start gap-0.5">
          <StatusBadge kind="convite" value={convite.status} />
          {convite.accepted_at ? (
            <span className="text-2xs text-ink-500">
              aceito em {formatDateTime(convite.accepted_at)}
            </span>
          ) : null}
          {convite.revoked_at ? (
            <span className="text-2xs text-ink-500">
              revogado em {formatDateTime(convite.revoked_at)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'validade',
      header: 'Vale ate',
      hideBelow: 'md',
      render: (convite) => (
        <span className="tabular-nums">{formatDateTime(convite.expires_at)}</span>
      ),
    },
    {
      key: 'acoes',
      header: '',
      align: 'right',
      render: (convite) => {
        const emAberto = convite.status === 'pendente' || convite.status === 'expirado';
        if (!emAberto) return null;
        return (
          <div className="flex justify-end gap-1">
            {podeConvidar ? (
              <AcaoComIcone
                acao="reenviar"
                rotulo="Reenviar"
                disabled={reenviar.isPending}
                onClick={() => reenviar.mutate(convite.id)}
              />
            ) : null}
            {podeRevogar ? (
              <AcaoComIcone
                acao="revogar"
                rotulo="Revogar"
                aria-label={`Revogar convite de ${convite.name}`}
                onClick={() => setRevogando(convite)}
              />
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Convites de acesso"
          description="Quem foi chamado para a empresa e ainda nao entrou."
          action={
            podeConvidar ? (
              <Button size="sm" onClick={() => setConvidando(true)}>
                Convidar pessoa
              </Button>
            ) : null
          }
        />

        {consulta.isError ? (
          <CardBody>
            <Alert tone="danger" title="Nao foi possivel carregar os convites">
              {consulta.error.message}
            </Alert>
          </CardBody>
        ) : null}

        <DataTable
          columns={colunas}
          items={consulta.data ?? []}
          keyOf={(convite) => convite.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="users"
              title="Nenhum convite emitido"
              description="Convide alguem para dar acesso sem passar por dentro do banco."
            />
          }
        />
      </Card>

      <p className="text-2xs text-ink-500">
        O convite vale por sete dias, serve uma vez so e pode ser revogado a qualquer
        momento. Reenviar troca o endereco: o anterior deixa de valer no mesmo instante,
        para que revogar nao dependa de lembrar quantas copias foram enviadas.
      </p>

      {convidando ? (
        <UserFormModal
          modo="criar"
          papeis={papeis}
          daCorretora={daCorretora}
          podeGerirPapeis={false}
          onClose={() => setConvidando(false)}
          onCriado={(resultado) => {
            setConvidando(false);
            setEmitido(resultado);
            invalidar();
          }}
        />
      ) : null}

      {emitido ? (
        <EnderecoDoConvite emitido={emitido} onClose={() => setEmitido(null)} />
      ) : null}

      <ConfirmDialog
        open={revogando !== null}
        onClose={() => setRevogando(null)}
        onConfirm={() => revogando && revogar.mutate(revogando.id)}
        title="Revogar convite"
        message={
          revogando
            ? `O endereco enviado a ${revogando.name} deixa de valer imediatamente. Para chamar a pessoa de novo, emita um convite novo.`
            : ''
        }
        confirmLabel="Revogar"
        tone="danger"
        loading={revogar.isPending}
      />
    </div>
  );
}

/**
 * Endereco do convite, exibido uma unica vez.
 *
 * Enquanto nao houver provedor de e-mail conectado, entregar o endereco e trabalho de
 * quem convidou. A tela diz isso em vez de sugerir que um e-mail saiu: alguem
 * esperando por uma mensagem que nunca foi enviada perde o prazo do convite.
 */
export function EnderecoDoConvite({
  emitido,
  onClose,
}: {
  emitido: InvitationIssued;
  onClose: () => void;
}) {
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(emitido.accept_url);
      setCopiado(true);
      toast.success('Endereco copiado.');
    } catch {
      // A area de transferencia pode estar bloqueada pelo navegador. O endereco
      // continua visivel e selecionavel na tela, entao o caminho manual segue aberto.
      toast.error('Nao foi possivel copiar. Selecione o endereco e copie a mao.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Convite emitido"
      description={emitido.invitation.email}
      footer={<Button onClick={onClose}>Concluir</Button>}
    >
      <div className="flex flex-col gap-4">
        {emitido.delivered_by_email ? (
          <Alert tone="success" title="Convite enviado por e-mail">
            A pessoa recebeu o endereco de acesso.
          </Alert>
        ) : (
          <Alert tone="warning" title="Envio automatico nao configurado">
            Nenhum provedor de e-mail esta conectado nesta instalacao. Copie o endereco
            abaixo e envie a pessoa pelo canal que voce ja usa.
          </Alert>
        )}

        <div>
          <p className="text-xs font-semibold text-ink-700">Endereco de acesso</p>
          <p className="mt-1 text-2xs text-ink-500">
            Ele aparece uma vez so e nao volta na lista de convites. Se voce perder, use
            Reenviar: o endereco anterior deixa de valer e um novo e criado.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              readOnly
              value={emitido.accept_url}
              aria-label="Endereco de acesso do convite"
              onFocus={(evento) => evento.currentTarget.select()}
              className="min-w-0 flex-1 rounded-md border border-line bg-surface-sunken px-3 py-2 font-mono text-2xs text-ink-800"
            />
            <Button variant="secondary" onClick={() => void copiar()}>
              {copiado ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
        </div>

        <p className="text-2xs text-ink-500">
          Quem abrir o endereco escolhe a propria senha. Ninguem que administra a
          empresa a define nem consegue enxerga-la.
        </p>
      </div>
    </Modal>
  );
}
